import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { ScoreProcessingBanner } from '@/components/dashboard/scoring';
import { InspectionPunchList } from '@/components/inspection/InspectionPunchList';
import { UploadReportInline } from '@/components/property/UploadReportModal';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { createDocuments } from '@/services/api/documents/api';
import { generatePreSignedUrls } from '@/services/api/files/api';
import { useTranslationPrefix } from '@/translations/i18n';

export const InspectionReportView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedProperty, propertyIssues, isInitialFetchingPropertyIssues } =
    usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const { isConditionProcessing } = useHauserEQResults(propertyId);
  const { stepProcessing, beginProcessingIndicator, resetProcessing } = useUploaderContext();
  const queryClient = useQueryClient();
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');

  const isReportProcessing = isConditionProcessing || (stepProcessing >= 1 && stepProcessing < 5);
  const hasNoReport =
    !isInitialFetchingPropertyIssues && (!propertyIssues || propertyIssues.length === 0);

  const handleUpload = async (propertyId: string, file: File) => {
    // Bootstrap the sidebar processing indicator immediately so the user sees
    // feedback during the 5–30s window before the first SNS progress event
    // bumps stepProcessing through stages 2–4.
    beginProcessingIndicator();
    try {
      const [presigned] = await generatePreSignedUrls({
        files: [
          {
            filename: file.name,
            mimeType: file.type || 'application/octet-stream',
            isExtractable: true,
          },
        ],
      });
      await fetch(presigned.preSignedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      await createDocuments({
        propertyId,
        documents: [
          {
            filename: file.name,
            fileSize: file.size,
            mimeType: file.type || 'application/octet-stream',
            s3Key: presigned.key,
          },
        ],
      });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['document-records', propertyId] });
      showAlert(t('report.uploaded'), 'success');
    } catch {
      resetProcessing();
      showAlert(t('report.upload_failed'), 'error');
    }
  };

  // Report was uploaded and is being processed — show processing state instead of upload CTA
  if (isReportProcessing && hasNoReport) {
    return (
      <Box
        data-testid="inspection-report-processing"
        sx={{
          p: isMobile ? '10px' : 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}>
        <ScoreProcessingBanner
          minHeight={300}
          message="Inspection report uploaded — processing"
          footer="Your report data will appear here shortly."
        />
      </Box>
    );
  }

  if (hasNoReport) {
    return (
      <Box
        data-testid="inspection-report-empty"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          {selectedProperty && (
            <UploadReportInline property={selectedProperty.property} onUpload={handleUpload} />
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box data-testid="inspection-report-view">
      <InspectionPunchList />
    </Box>
  );
};
